import './AbroadCityCard.css';
import { useId } from 'preact/hooks';
import { CandidateRow } from '../../components/CandidateRow';
import { Chip } from '../../components/Chip';
import { SectionLabel } from '../../components/SectionLabel';
import { UfTile } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { fmtPct, fmtVoters } from '../../format/numbers';
import { placeName } from '../../format/places';
import { fmtHourShort } from '../../format/time';
import { cityRows } from '../../model/abroad';
import { abroadTally2022, ref2022Of } from '../../model/ref2022';
import { openProfile } from '../../state/actions';
import {
  useConfig,
  useHeadline,
  useLayout,
  useNowMinute,
  usePhase,
  useRouteState,
  useStatics,
  useTimeline,
  useView,
} from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';
import { PlaceCard } from './PlaceCard';
import { PlaceHeader } from './PlaceHeader';
import { UfNavButtons } from './UfNavButtons';
import { UpLink } from './UpLink';
import { useStateDetail } from './useStateDetail';

const pct1 = (v: number): string => fmtPct(v, 1);

/**
 * Cartão da cidade do exterior (§8.5 AbroadCityCard): "‹ {País}" (ou "‹ Exterior" quando o país tem uma cidade só),
 * o ladrilho com o ISO, "{País} · {eleitores}", o presidente na cidade (barra e votos), a nota do fechamento das urnas
 * no horário de Brasília com as seções, e a linha "2022". O resultado vem do detalhe de `ZZ`. Neutralidade: 13 | 22.
 */
export function AbroadCityCard() {
  const route = useRouteState();
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const layout = useLayout();
  const phase = usePhase();
  const headline = useHeadline();
  const { live, displayTMin } = useTimeline();
  // o instante do fechamento das urnas: ao vivo, o relógio da fonte (antes do 1º número a linha do tempo não anda);
  // revendo a noite, o instante exibido (§8.5 "Dados")
  const nowTMin = useNowMinute();
  const instant = live ? nowTMin : displayTMin;
  const id = useId();
  const seq = view?.seq ?? null;
  const detail = useStateDetail('ZZ', live, seq);
  const p = route.place.kind === 'abroad_city' ? route.place : null;
  const geo = statics.geography;
  const city = geo?.abroad_cities.find((c) => c.tse === p?.tse) ?? null;
  if (p === null) return null;

  const country = geo?.countries.find((c) => c.iso2 === p.iso2) ?? null;
  const tv = layout.mode === 'tv';
  const pair = view?.candidates ?? statics.candidates?.president ?? null;
  const photos = config.features.photos;
  const beforeOpen = phase === 'pre_open' || headline === 'countdown';
  const row = city ? (cityRows([city], detail?.value, view?.abroad, pair, instant)[0] ?? null) : null;
  const votes = !beforeOpen && row?.votes && row.votes[0] + row.votes[1] > 0 ? row.votes : null;
  const counted = row?.sectionsCounted ?? null;
  const chip =
    beforeOpen || counted === 0 || (detail?.status === 'ok' && counted === null)
      ? 'waiting'
      : row && counted !== null && counted >= row.sections
        ? 'counted'
        : null;
  const h = city ? fmtHourShort(city.closes_t_min) : '';
  const note = beforeOpen
    ? t('common.votesFrom17')
    : !live
      ? t('country.replay')
      : row && counted !== null
        ? t(row.closed ? 'city.note' : 'city.note.open', { h, N: counted, M: row.sections })
        : detail?.status === 'error'
          ? t('country.error')
          : detail?.status === 'ok'
            ? // o detalhe chegou sem a cidade: sem seção totalizada, nunca "carregando" sem fim
              t('uf.noSections')
            : t('country.loading');
  const ref =
    votes && pair && row?.shares && city
      ? ref2022Of(statics.baseline, abroadTally2022(statics.baseline, city.idx), pair, row.shares)
      : null;
  // o país com uma cidade só não tem cartão próprio que valha a pena: o "‹" volta ao Exterior (AC-8.5-05)
  const single = (country?.n_cities ?? 1) <= 1;

  return (
    <PlaceCard kind="abroad-city" class="abroad-city-card" state={votes ? 'votes' : 'no_votes'}>
      <UpLink
        to={single ? placeName('ZZ') : (country?.name ?? p.iso2)}
        label={single ? t('place.up.aria.abroad') : undefined}
      />
      <PlaceHeader
        title={city?.name ?? p.tse}
        sub={city ? t('city.sub', { PAIS: country?.name ?? p.iso2, E: fmtVoters(city.electorate_ref) }) : null}
        seal={<UfTile code={p.iso2} fill="plain" size="big" />}
        buttons={tv ? 0 : 1}
      />
      <UfNavButtons kind="abroad-city" />
      <section class="place-card__section" data-testid="abroad-city-card-president" aria-labelledby={`${id}-president`}>
        <SectionLabel
          id={`${id}-president`}
          label={t('tabs.president')}
          actions={
            chip === 'counted' ? (
              <Chip tone="done" label={t('chip.counted')} />
            ) : chip === 'waiting' ? (
              <Chip label={t('chip.waiting')} />
            ) : undefined
          }
        />
        <p class="place-note" data-testid="abroad-city-card-note">
          {note}
        </p>
        {votes && pair && row?.shares ? (
          <div class="abroad-city-card__rows">
            {pair.map((c, i) => (
              <CandidateRow
                key={c.number}
                candidate={c}
                photo={photos ? c.photo : null}
                share={row.shares?.[i as 0 | 1] ?? null}
                votes={votes[i as 0 | 1]}
                variant="bar-votes"
                portraitSize={30}
                onOpen={tv ? undefined : () => openProfile(c.number, 'president', 'list')}
              />
            ))}
          </div>
        ) : null}
        {ref && !tv ? (
          <p class="place-ref" data-testid="abroad-city-card-2022" title={t('compare.2022.title')}>
            <span class="place-ref__label">{t('compare.2022.label')}</span>
            {ref.items.map((it, i) => (
              <span key={it.number} class={`place-ref__item ${partyClass(partyOf(it.party_code))}`}>
                {i > 0 ? <span class="sr-only">, </span> : null}
                <span class="place-ref__swatch" data-dataviz="" aria-hidden="true" />
                {t('compare.2022.item', { NOME2022: it.name, P: pct1(it.share) })}
              </span>
            ))}
          </p>
        ) : null}
      </section>
    </PlaceCard>
  );
}
